import "./styles.css"

import React, { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Dropdown, MenuProps, Space } from "antd"
import clsx from "clsx"
import { uniqueId } from "lodash"
import { Menu } from "react-feather"

import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"

import API from "api"
import { ROUTES } from "../../routes"
import { getLanguage, setLanguage, useLanguage } from "@languages/index"
import { debounce } from "../../helpers/debounce"
import { isEmpty } from "../../helpers/object"

import BRAND from "../../constants/brand"
import { LOCALES } from "constants/locales"
import { isGlovvJp, isGlovvKr, isKyolook, isOneulook } from "constants/domains/domains"
import { getServiceName } from "constants/service/service"

import Label from "../label"

interface Props {
  className?: string
  backgroundColor?: any
  textColor?: any
  activeTextColor?: any
  fontSize?: any
  auth?: any
  brand?: any
  preferences?: any

  sidebar?: any
  popup?: any
  sheet?: any

  setSidebar?: any
  setPreferences?: any
  setSettings?: any
  showModal?: (params: any) => void
  setSheet?: (params: any) => void
  closeSheet?: () => void
  setPopup?: (params: any) => void
  closePopup?: () => void
  closeOverlays?: () => void
  logout?: () => void
}

const Header = (props: Props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.header }

  const navigate = useNavigate()
  const location = useLocation()
  const headerRef = useRef<any>()

  const textColor = props.textColor ? props.textColor : "#999999"
  const activeTextColor = props.activeTextColor ? props.activeTextColor : "black"

  const isWholesale = props.brand.businessType === BRAND.BUSINESS_TYPE.WHOLESALE
  const isNotAvailableInJapan = process.env.LOCALE == LOCALES.JA
  const isLectureOnlyAccount = BRAND.isLectureOnlyBrand(props.brand.id)
  const isBeautyCategoryBrand = props.brand?.isCategoryBeauty
  const isMaster = props.auth?.isMaster

  const beautyIntroPageUrl =
    process.env.LOCALE == LOCALES.JA
      ? `https://glovv.co.kr/japan`
      : `https://glovv.co.kr/?isVisibleContactForm=0`

  const brandMenus = [
    {
      hidden:
        isWholesale ||
        isLectureOnlyAccount ||
        props.brand.hasExperiencedInfoUpdate ||
        props.brand.hasExperiencedAlarmUpdate ||
        props.brand.hasExperiencedPasswordUpdate,
      name: languageTexts.is_first_visit,
      url: `/help/missions`,
    },
    {
      name: languageTexts.brand_info,
      url: "/settings/brand",
      isNew: false,
      hidden: isLectureOnlyAccount,
      children: [
        {
          name: languageTexts.setting_brand,
          url: "/settings/brand",
          hidden: isWholesale,
        },
        { name: languageTexts.setting_alarm, url: "/settings/brand/alarm" },
        {
          name: languageTexts.change_password,
          url: "/settings/brand/password",
        },
      ],
    },
    {
      hidden: isWholesale || isNotAvailableInJapan || isBeautyCategoryBrand || isLectureOnlyAccount,
      name: languageTexts.rent_product,
      url: ROUTES.RENTAL_INDEX.PATH,
      children: isLectureOnlyAccount
        ? undefined
        : [
            // { name: languageTexts.introduce, url: ROUTES.RENTAL_INTRO.PATH },
            { name: languageTexts.create_n_manage, url: ROUTES.RENTAL_INDEX.PATH },
          ],
    },
    {
      hidden: isLectureOnlyAccount,
      name: languageTexts?.campaign,      
      url: ROUTES.CAMPAIGN_INTRO.PATH,
      children: isLectureOnlyAccount
        ? undefined
        : [
            // {
            //   name: languageTexts.introduce,
            //   url: isBeautyCategoryBrand ? beautyIntroPageUrl : ROUTES.CAMPAIGN_INTRO.PATH,
            // },
            {
              name: languageTexts.create_n_manage,
              url: ROUTES.CAMPAIGN_INDEX.PATH,
              hidden: isLectureOnlyAccount,
            },
            {
              name: languageTexts.payment_history1,
              url: ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH,
              hidden: isLectureOnlyAccount,
            },
          ],
    },
    {
      hidden: isWholesale || isNotAvailableInJapan || isBeautyCategoryBrand || isLectureOnlyAccount,
      isBest: false,
      name: languageTexts.connect_company_mall,
      url: ROUTES.SNS_INTRO.PATH,
      children: props.brand.remainSnsPass
        ? [
            {
              name: languageTexts.introduce,
              url: ROUTES.SNS_INTRO.PATH,
            },
            {
              name: languageTexts.connect_n_manage,
              url: ROUTES.SNS_INDEX.PATH,
              hidden: isLectureOnlyAccount,
            },
            {
              name: languageTexts.payment_history1,
              url: ROUTES.SNS_PAYMENTS_INDEX.PATH,
              hidden: isLectureOnlyAccount,
            },
          ]
        : undefined,
    },
    {
      hidden: isWholesale || isLectureOnlyAccount || isBeautyCategoryBrand,
      // isBest: false,
      name: languageTexts.invite_offline,
      url: ROUTES.INVITE_INTRO.PATH,
    },
    {
      hidden: isWholesale || isLectureOnlyAccount,
      name: languageTexts?.influencer_page,
      url: ROUTES?.INFLUENCER_PAGE?.PATH,   
    },

    {
      name: languageTexts.posting,
      url: "/postings",
      hidden: isLectureOnlyAccount,
    },
    // {
    //   hidden: isWholesale || isNotAvailableInJapan,
    //   name: languageTexts.report,
    //   url: `/reports`,
    // },
    {
      isBest: true,
      hidden: isNotAvailableInJapan,
      name: languageTexts.lectures,
      url: ROUTES.LECTURE_INDEX.PATH,
    },
    {
      isBest: true,
      hidden: isNotAvailableInJapan || isLectureOnlyAccount,
      name: languageTexts.japan,
      url: ROUTES.JAPAN.PATH,
    },
    {
      hidden: !isKyolook(),
      name: languageTexts.transaction_info,
      url: ROUTES.TRANSACTION_INFO_INDEX.PATH,
    },
  ]

  const accountMenus = [
    {
      name: languageTexts.manage_card,
      url: "/cards",
      hidden: isLectureOnlyAccount,
    },
    isBeautyCategoryBrand ? {
      name: languageTexts.payment_history1,
      url: ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH,
      hidden: isLectureOnlyAccount,
    } : {
      name: languageTexts.payment_history1,
      url: "/payments",
      hidden: isLectureOnlyAccount,
    },
    {
      name: languageTexts.logout,
      url: `/logout`,
    },
  ]

  const [brandName, setBrandName] = useState(undefined)
  const [selected, setSelected] = useState<any>()

  const [isTablet, setIsTablet] = useState(false)

  useEffect(() => {
    if (props.preferences?.brandName && !brandName) {
      setBrandName(props.brand.name)
    }
  }, [props.preferences])

  useEffect(() => {
    if (isKyolook()) {
      console.debug("[Kyolook]", isKyolook())
    }
    if (isOneulook()) {
      console.debug("[Oneulook]", isOneulook())
    }
    if (isGlovvKr()) {
      console.debug("[GlovvKr]", isGlovvKr())
    }
    if (isGlovvJp()) {
      console.debug("[GlovvJp]", isGlovvJp())
    }
  }, [])

  const navigateWithScroll = (routePath, topOffset = 0) => {
    const scrollToOptions = { top: topOffset, left: 0, behavior: "instant" }
    window.scrollTo(scrollToOptions as unknown as ScrollToOptions) // Temporary solved Typescript bug (https://github.com/microsoft/TypeScript/issues/47441)
    navigate(routePath)
  }

  const handleTabClick = (tab: any) => {
    setSelected(tab)
    // if (tab.children && tab.children.length > 0) {
    //   return false
    // }

    if (tab.children && tab.children.length > 1 && tab.children.filter((child: any) => !child.hidden).length > 1) {
      return false
    }

    if (tab.url == "/logout") {
      props.logout()
      props.setSidebar({ isVisible: false })
      navigateWithScroll("/")
      return false
    }

    redirect(tab.url)
    setSelected(undefined)
  }

  const redirect = (url) => {
    if (url.startsWith("http://brand.nmodelin.com/") || url.startsWith("http://3.38.150.13:3339/")) {
      window.location.href = url
      return false
    }

    if (
      url.startsWith("https://oneulook.com") ||
      url.startsWith("https://kyolook.com") ||
      url.startsWith("https://glovv.co.kr")
    ) {
      window.open(url, "_blank")
      return false
    }

    navigateWithScroll(url)
  }

  // @todo header, rootEventListener, sidebar. 필요하면 통합
  const getBrandCurrencyText = () => {
    const serverLocale = process.env.LOCALE
    const userLocale = getLanguage() ?? props?.preferences?.locale
    const isJpy = props.brand.currency === "JPY"
    const isKrw = props.brand.currency === "KRW"
    const provider = isJpy ? "JPY" : "KRW"

    const currencyMap = {
      JPY: {
        ko: "엔",
        ja: "円",
        default: { ko: "엔", ja: "円" },
      },
      KRW: {
        ko: "원",
        ja: "ウォン",
        default: { ko: "원", ja: "ウォン" },
      },
    }

    if (isJpy || isKrw) {
      return currencyMap[provider][userLocale] || currencyMap[provider].default[serverLocale]
    }

    return currencyMap[provider].default[serverLocale] || currencyMap[provider].default[props.preferences.locale]
  }

  const renderBrandMenus = () => {
    return (
      <>
        <div className="flex flex-row items-center pl-4 xl:pl-7 xl:text-black">
          <div className="flex flex-col">
            {isKyolook() && (
              <div className="leading-[16px]">
                <span className="text-[12px] font-bold">{getServiceName()}</span>
              </div>
            )}
            <div
              className={clsx([
                "brand-name flex items-center",
                {
                  "brand-name-compact": isKyolook(),
                },
              ])}
            >
              <span className="mr-7 xl:mr-2 text-black">{`${props.brand?.name}`}</span>
              <span className="hidden xl:flex xl:mr-7 text-black">{languageTexts.manager_page}</span>
            </div>
          </div>
          {brandMenus.map((menu, index) => {
            if (menu.hidden) return null

            const isActive = location.pathname.startsWith(
              menu.url.replace("http://3.38.150.13:3339", "").replace("http://brand.nmodelin.com/", "")
            )

            return (
              <div
                key={`brandMenu${index}`}
                className={clsx({
                  hidden: true,
                  relative: true,
                  "text-center": true,
                  "px-3": true,
                  "tab-title": true,
                  "xl:flex": isTablet ? false : true,
                  "text-[#000000]": isActive ? true : false,
                  "font-bold": isActive ? true : false,
                  "hover:font-bold": true,
                  "hover:text-[#000000]": true,
                  "cursor-pointer": true,
                  "flex flex-row": true,
                  "h-full": true,
                  "items-center": true,
                  "py-4 py- xl:py-6": true,
                })}
                onMouseEnter={() => {
                  setSelected(menu)
                }}
                onPointerEnter={() => {
                  setSelected(menu)
                }}
                onPointerLeave={() => {
                  setSelected(null)
                }}
                onMouseLeave={() => {
                  setSelected(null)
                }}
                onClick={() => {
                  handleTabClick(menu)
                }}
              >
                <span>{menu.name}</span>
                {menu.isNew ? (
                  <Label className="ml-2 rounded-sm px-2 py-1 " textClassName="font-medium text-sm" text="NEW" />
                ) : null}

                 {menu.url === ROUTES?.INFLUENCER_PAGE?.PATH ? (
                  <div
                    className="relative ml-2 font-medium text-sm"
                    style={{
                      top: "-0.8rem",
                    }}
                  >
                    <img className="w-[40px] h-[20px]" src="/assets/icons/ic-balloon-small-2.svg" />
                    <span
                      className="absolute inset-0 w-full h-full text-white font-bold tracking-tighter text-center text-[12px]"
                      style={{
                        left: " 0.05rem",
                        top: 0,
                      }}
                    >
                      {languageTexts?.new}
                    </span>
                  </div>
                ) : null}

                {/* {menu.isBest ? (
                  <div
                    className="relative ml-2 font-medium text-sm"
                    style={{
                      top: "-0.8rem",
                    }}
                  >
                    <img className="w-[40px] h-[20px]" src="/assets/icons/ic-balloon-small-2.svg" />
                    <span
                      className="absolute inset-0 w-full h-full text-white font-bold tracking-tighter text-center text-[12px]"
                      style={{
                        left: "0.05rem",
                        top: 0,
                      }}
                    >
                      {languageTexts.popularity}
                    </span>
                  </div>
                ) : null} */}

                {selected?.url === menu.url && menu.children && menu.children.length ? (
                  <div
                    className="absolute flex flex-col gap-4 menu-shadow rounded-lg py-3 items-center max-w-[130px] z-[4]"
                    style={{
                      top: "3.85rem",
                      left: "-15px",
                      right: "-15px",
                    }}
                  >
                    {menu.children.map((child) => {
                      if (child.hidden) return null

                      const isActive = child.url.endsWith(location.pathname) ? "active" : null

                      return (
                        <div
                          key={`header-menu-${uniqueId()}`}
                          className={clsx({
                            flex: true,
                            "w-full": true,
                            "justify-center": true,
                            "max-w-[120px]": true,
                            "text-gray-400": isActive ? false : true,
                            "text-[#000000]": isActive ? true : false,
                            "font-bold": isActive ? true : false,
                            "hover:font-bold hover:text-[#000000]": true,
                            "animate__animated animate__fadeIn": true,
                          })}
                          onClick={() => {
                            handleTabClick(child)
                          }}
                        >
                          <span>{child.name}</span>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </>
    )
  }

  const renderLanguageSelector = () => {
    const handleButtonClick = async (nextLocale) => {
      props.setPreferences({ locale: nextLocale })
      props.setSettings({ currencyText: getBrandCurrencyText() })
      setLanguage(nextLocale)
      const url = window.location.pathname
      navigate("/-1", { replace: true })

      const response = await API.Brand.update({
        brandToken: props.auth.token,
        locale: nextLocale,
      })

      setTimeout(() => {
        navigate(url, { replace: true })
      }, 300)
    }

    const items: MenuProps["items"] = [
      {
        style: { minWidth: "120px" },
        key: "ja",
        label: languageTexts.locale_japan,
        onClick: () => {
          handleButtonClick(LOCALES.JA)
        },
        icon: <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ja.svg"} />,
      },
      {
        style: { minWidth: "120px" },
        key: "ko",
        label: languageTexts.locale_korea,
        onClick: () => {
          handleButtonClick(LOCALES.KO)
        },
        icon: <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ko.svg"} />,
      },
    ]

    let locale = props.preferences?.locale

    return (
      <Space wrap>
        <Dropdown menu={{ items }} placement="bottomLeft">
          <div className="flex items-center justify-center text-center">
            {!locale ? (
              <img className="w-[16px] h-[16px]" src={`/assets/icons/flags-${process.env.LOCALE}.svg`} />
            ) : null}
            {locale == LOCALES.KO ? <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ko.svg"} /> : null}
            {locale == LOCALES.JA ? <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ja.svg"} /> : null}
          </div>
        </Dropdown>
      </Space>
    )
  }

  const renderAccountMenus = () => {
    return (
      <div className={clsx(["px-5 hidden items-center", isTablet ? null : "xl:flex"])}>
        <div className="mr-auto flex flex-row py-5">
          {accountMenus.map((menu, index) => {
            if (menu.hidden) return null

            const isActive = (
              menu.url !== ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH &&
              location.pathname === menu.url
            )

            return (
              <div className="gnb-item -item mr-5 cursor-pointer" key={`accountMenus${index}`}>
                <a
                  id="main_menu"
                  className={`tab-title ${isActive ? "active" : null}`}
                  style={{
                    color: isActive ? activeTextColor : textColor,
                  }}
                  onClick={() => {
                    handleTabClick(menu)
                  }}
                >
                  {menu.name}
                </a>
              </div>
            )
          })}
          {renderLanguageSelector()}
        </div>
      </div>
    )
  }

  const headerClass = clsx(
    "header w-full flex justify-center left-0 right-0 top-0 bg-[white] xl:bg-white z-[4] max-h-[60px] md:max-h-[76px] overflow-visible bg-white"
  )

  const renderHamburgButton = () => {
    const toggleSidebar = () => {
      props.setSidebar({ isVisible: !props.sidebar.isVisible })
    }

    return (
      <div
        className={clsx(["ml-auto flex items-center cursor-pointer", isTablet ? null : "xl:hidden"])}
        onClick={toggleSidebar}
      >
        <div className="hamburg flex items-center  p-4 xl:p-7 text-black xl:text-black">
          <Menu />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (headerRef.current && props.preferences.headerHeight != headerRef.current.clientHeight) {
      handleResize()
      props.setPreferences({
        headerHeight: headerRef.current.clientHeight,
      })
    }
  }, [headerRef.current])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setSelected(null)
      }
    }

    document.body.addEventListener("click", handleClickOutside)

    return () => {
      document.body.removeEventListener("click", handleClickOutside)
    }
  }, [])

  const handleResize = () => {
    debounce(() => {
      const isNextLayoutMobile = window.innerWidth < 800

      let nextPreferences = {
        isMobile: isNextLayoutMobile,
      }

      if (headerRef.current && props.preferences.headerHeight != headerRef.current.clientHeight) {
        nextPreferences = {
          ...nextPreferences,
          ...{ headerHeight: headerRef.current.clientHeight },
        }

        // document.body.style.maxHeight = `${window.innerHeight}px`
      }

      props.setPreferences(nextPreferences)
    }, 400)
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const isIPad = navigator.userAgent.match(/iPad/i) || (navigator.userAgent.match(/Mac/) && "ontouchend" in document)
    const isAndroidTablet = navigator.userAgent.match(/Android/i) && !navigator.userAgent.match(/Mobile/i)
    setIsTablet(!!(isIPad || isAndroidTablet))
  }, [])

  const currentLayout = useRef(props.preferences.isMobile)

  useEffect(() => {
    if (props.popup && props.popup.isVisible) {
      props.setSheet(props.popup)
      props.setPopup({})
    }

    if (props.sheet && props.sheet.isVisible) {
      props.setPopup(props.sheet)
      props.setSheet({})
    }
    currentLayout.current = props.preferences.isMobile
  }, [props.preferences.isMobile])

  if (isEmpty(props.auth) || isEmpty(props.brand)) {
    return <></>
  }

  return (
    <div
      className={headerClass}
      ref={headerRef}
      style={{
        minHeight: "60px",
      }}
    >
      <div
        className="flex columns-1 xl:columns-2 xl:flex-row justify-between w-full divider bg-white"
        style={{
          borderBottom: "1px solid #F4F4F4",
        }}
        onLoad={(event) => {
          props.setPreferences({
            headerHeight: event.currentTarget.clientHeight,
          })
        }}
      >
        {renderBrandMenus()}
        {renderAccountMenus()}
        {renderHamburgButton()}
      </div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(Header)
